import { map, startWith } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import {
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    Document,
    DocumentFieldOption,
    FieldSchema,
    DocumentService,
    ProspectData,
    StageSchema
} from '@lsq-frontend/remote-services/ar-app';
import {
    Color,
    Icons,
    DateFormat,
    Modal,
    ModalService,
    ModalServiceToken,
    TableAppearance
} from '@lsq-frontend/common-ui';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

import { AbstractField } from '../dr-abstract-field/dr-abstract-field';
import { IUploadData } from './dr-document-field.component.interface';
import { RemoveDocumentModal } from './remove-document-modal/remove-document-modal.component';
import { UploadDocumentModal } from './upload-document-modal/upload-document-modal.component';
import { AddDocumentModal } from '../dr-add-document-field/add-document-modal/add-document-modal.component';
import { DrApplicationService } from '../../service/dr-application-service';
import { documentRequestForm } from '../../forms/document-forms';

@Component({
    selector: 'dr-document-field',
    styleUrls: ['./dr-document-field.component.scss'],
    templateUrl: './dr-document-field.component.html',
    encapsulation: ViewEncapsulation.None
})
export class DocumentField extends AbstractField implements OnInit {
    @Input() fieldSchema: FieldSchema;
    @Input() stageSchema: StageSchema;
    @Input() formGroup: FormGroup;
    @Input() field: { fieldValue: any };
    @Input() adminMode: Boolean;
    @Input() schemaType: string;

    public fieldOption: DocumentFieldOption;
    public readonly icons = Icons;
    public appearance = TableAppearance.Default;
    public hasDocumentFields = false;
    public showRequestDetails: Boolean = false;
    public color = Color;
    public prospectId = '';
    public uploadFormData: IUploadData = {
        note: '',
        file: {} as File,
        asOfDate: '',
        endDate: ''
    };
    public documents: Document[];
    public dateFormat = DateFormat;
    public showAcceptButton: Boolean = false;
    public showSubmitButtons: Boolean = false;
    public processing: Boolean = false;

    constructor(
        @Inject(ModalServiceToken) private _modalService: ModalService,
        private _changeDetectorRefs: ChangeDetectorRef,
        private _documentService: DocumentService,
        private _arService: DrApplicationService,
        private _alertService: AlertService
    ) {
        super();
        this.prospectId = this._arService.getProspect().id;
    }

    public ngOnInit(): void {
        this.fieldOption = this.fieldSchema.fieldOption;
        this.showRequestDetails = !!this.fieldOption.requestedAt || !!this.fieldOption.requestedBy;
        this.loadDocumentsInTable();
        this.showAcceptButton =
            this.adminMode &&
            this.hasDocumentFields &&
            this.stageSchema?.id === this._arService.getOpenDocumentsStageID();
        this.showSubmitButtons =
            !this.processing &&
            (this.adminMode ||
                this.stageSchema?.id !== this._arService.getHistoricalDocumentsStageID());
    }

    public loadDocumentsInTable(): void {
        const docs = (this.field?.fieldValue as Document[]) ?? [];
        this.updateDocumentDataSource(docs);
    }

    public updateDocumentDataSource(documents: Document[]): void {
        this.hasDocumentFields = documents.length !== 0;
        this.documents = documents;
        this._changeDetectorRefs.markForCheck();
    }
    public async uploadDocument(
        uploadData: File,
        documentNotes: string,
        prospectId: string,
        stageSchemaId: string,
        fieldSchemaId: string,
        asOfDate: string,
        endDate: string
    ): Promise<ProspectData> {
        const uploadDocumentForm = this._documentService.uploadDocumentForm(
            uploadData,
            documentNotes,
            prospectId,
            stageSchemaId,
            fieldSchemaId,
            uploadData.name,
            asOfDate,
            endDate
        );
        try {
            const uploadResponse = await uploadDocumentForm.toPromise();
            return uploadResponse;
        } catch (err) {
            throw err;
        }
    }

    public async uploadFile(): Promise<void> {
        let uploadResponse;
        try {
            const stageSchemaDocumentId = this._arService.getDocumentsStageID();
            uploadResponse = await this.uploadDocument(
                this.uploadFormData.file,
                this.uploadFormData.note,
                this.prospectId,
                stageSchemaDocumentId,
                this.fieldSchema.id,
                this.uploadFormData.asOfDate,
                this.uploadFormData.endDate
            );
            if (uploadResponse) {
                this._arService.safeUpdateStageData(stageSchemaDocumentId, uploadResponse);
                this.loadDocumentsInTable();
            }
        } catch (error) {
            this._alertService.error('Error uploading document. Please try again');
        }
        this._modalService.closeModal();
    }

    public handleUploadDocument(): void {
        const fileLimit = this._arService.getProspectSchema().fileSizeLimitMB;
        this._modalService.showModal(
            new Modal({
                id: 'upload-document',
                title: 'Upload Document',
                component: UploadDocumentModal,
                showCloseButton: true,
                data: {
                    ...this.fieldSchema,
                    field: this.field,
                    prospectId: this.prospectId,
                    uploadData: this.uploadFormData,
                    fileSizeLimit: fileLimit
                },
                width: '58rem',
                onOkClick: async () => {
                    this._arService.visitField(this.fieldSchema.id);
                    await this.uploadFile();
                }
            })
        );
    }

    public openModalRemoveFile(document: Document, documentLabel: string): void {
        const removeSingleFile = true;
        this._modalService.showModal(
            new Modal({
                id: 'remove-document',
                title: 'Confirm File Removal',
                component: RemoveDocumentModal,
                showCloseButton: true,
                data: { document, documentLabel, removeSingleFile },
                width: '58rem',
                onOkClick: async () => {
                    this._arService.visitField(this.fieldSchema.id);
                    await this.removeFile(document);
                }
            })
        );
    }

    public async removeFile(document: Document): Promise<void> {
        const stageSchemaDocumentId = this._arService.getDocumentsStageID();
        const deleteDocument = this._documentService.deleteDocument(
            document.documentId,
            this.prospectId,
            stageSchemaDocumentId,
            this.fieldSchema.id
        );
        try {
            const deleteResponse = await deleteDocument.toPromise();
            if (deleteResponse) {
                this._arService.safeUpdateStageData(stageSchemaDocumentId, deleteResponse);
                this.loadDocumentsInTable();
            }
        } catch (error) {
            this._alertService.error('Error removing file from document. Please try again');
        }
        this._modalService.closeModal();
    }

    public openModalRemoveDocument() {
        if (!this.hasDocumentFields) {
            const documentLabel = this.fieldSchema.label;
            const removeSingleFile = false;
            this._modalService.showModal(
                new Modal({
                    id: 'remove-document',
                    title: 'Confirm Document Removal',
                    component: RemoveDocumentModal,
                    showCloseButton: true,
                    data: { document, documentLabel, removeSingleFile },
                    width: '58rem',
                    onOkClick: async () => {
                        await this.removeDocument();
                    }
                })
            );
        }
    }

    public async removeDocument(): Promise<void> {
        const stageSchemaDocumentId = this._arService.getDocumentsStageID();
        const deleteDocumentRequest = this._documentService.deleteDocumentRequest(
            this.prospectId,
            stageSchemaDocumentId,
            this.fieldSchema.id
        );
        try {
            const deleteDocumentRequestResponse = await deleteDocumentRequest.toPromise();
            if (deleteDocumentRequestResponse) {
                this._arService.setProspectSchema(deleteDocumentRequestResponse);
            }
        } catch (error) {
            this._alertService.error('Error removing requested document. Please try again');
        }
        this._modalService.closeModal();
    }

    public handleEditDocument(): void {
        const editDocumentFormGroup: FormGroup = documentRequestForm(
            this.hasDocumentFields,
            this.fieldSchema
        );
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        const okButtonIsDisabled$ = editDocumentFormGroup.valueChanges.pipe(
            map((_) => !editDocumentFormGroup.valid),
            startWith(!editDocumentFormGroup.valid)
        );

        var data = {
            formGroup: editDocumentFormGroup,
            fieldSchema: this.fieldSchema,
            schemaType: this.schemaType
        };

        this._modalService.showModal(
            new Modal({
                title: 'Edit Document',
                bodyComponent: AddDocumentModal,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Update',
                okButtonIsDisabled$,
                okButtonSize: 'block',
                showCancelButton: true,
                clickOutsideToClose: false,
                data: data,
                isSubmitting$: isSubmittingSubject,
                onCancelClick: () => this._modalService.closeModal(),
                onOkClick: async () => {
                    if (editDocumentFormGroup.valid) {
                        isSubmittingSubject.next(true);
                        await this.updateDocument(editDocumentFormGroup);
                        isSubmittingSubject.next(false);
                    }
                }
            })
        );
    }

    public async updateDocument(formGroup: FormGroup): Promise<void> {
        //Update fields from form
        const updatedDocument = this.fieldSchema;
        var docFieldOption: DocumentFieldOption = updatedDocument.fieldOption as DocumentFieldOption;
        updatedDocument.label = formGroup.value.documentName;
        updatedDocument.name = formGroup.value.documentName.toLowerCase().replace(/\s/g, '');
        docFieldOption.copy = formGroup.value.documentDescription;
        docFieldOption.fileExtensions = formGroup.value.fileFormat;
        docFieldOption.scheduleRequest = formGroup.value.scheduleRequest || false;
        docFieldOption.startDate = formGroup.value.startDate || null;
        docFieldOption.repeatEvery = formGroup.value.repeatEvery;
        docFieldOption.repeats = formGroup.value.repeats;
        updatedDocument.fieldOption = docFieldOption;

        const stageSchemaDocumentId = this._arService.getDocumentsStageID();
        const updateDocumentRequest = this._documentService.updateDocumentRequest(
            updatedDocument,
            this.prospectId,
            stageSchemaDocumentId
        );
        try {
            const updateDocumentRequestResponse = await updateDocumentRequest.toPromise();
            if (updateDocumentRequestResponse) {
                this._arService.setProspectSchema(updateDocumentRequestResponse);
            }
        } catch (error) {
            this._alertService.error('Error updating document request. Please try again');
        }
        this._modalService.closeModal();
    }

    public validateDocumentField(): boolean {
        const hasDocs = ((this.field?.fieldValue as Document[]) ?? []).length > 0;
        return (
            this.fieldSchema.isRequired &&
            this._arService.isFieldVisited(this.fieldSchema.id) &&
            !hasDocs
        );
    }

    public async downloadDocument(doc: Document): Promise<void> {
        let documentBase64 = 'text';
        const downloadDocument = this._documentService.downloadDocument(
            doc.documentId,
            this._arService.getProspect().id
        );
        documentBase64 = await downloadDocument.toPromise();
        const downloadLink = document.createElement('a');
        downloadLink.href = 'data:application/octet-stream;base64,' + documentBase64;
        downloadLink.target = '_blank';
        downloadLink.setAttribute('download', doc.fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
    }

    public async handleAcceptDocument(): Promise<void> {
        this.processing = true;
        try {
            await this._arService.acceptDocument(this.fieldSchema.id);
        } finally {
            this.processing = false;
        }
    }
}
